import { CarouselItemVariants, CarouselVariants } from 'constants/carousel';
import { useEffect, useState } from 'react';
import BeforeAfter from './ItemVariants/BeforeAfter';
import Default from './ItemVariants/Default';
import { default as AlternateCarousel } from './Variants/Alternate';
import { default as DefaultCarousel } from './Variants/Default';
import type { ReactNode } from 'react';
import type { Slide } from 'types/Utilities';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';

type Props = {
  children?: ReactNode | ReactNode[];
  counter?: boolean;
  expand?: boolean;
  isMobile?: boolean;
  itemVariant: 'default' | 'before_after';
  locale: string;
  onClose?: () => void;
  onFilterChange: (option: Record<string, string>) => void;
  slides: Slide[];
  startSlide?: number;
  thumbnails?: boolean;
  variant: 'default' | 'alternate';
};

const Carousel = ({
  itemVariant,
  onClose,
  slides,
  variant,
  ...props
}: Props): JSX.Element | null => {
  const [originalSlides] = useState<Slide[]>(slides);
  const [carouselSlides, setCarouselSlides] = useState<Slide[]>(slides);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAllImagesLoaded] = useState(false);

  useEffect(() => {
    setIsLoading(false);
    window.addEventListener('keydown', keyListener);

    return () => {
      window.removeEventListener('keydown', keyListener);
    };
  }, []);

  const handleEsc = (): void => {
    onClose?.();
  };

  const keyListenersMap = new Map([['Escape', handleEsc]]);

  const keyListener = (event: KeyboardEvent): void => {
    keyListenersMap.get(event.code)?.();
  };

  /**
   * Resolve the component by the carousel variant
   *
   * @returns JSX.Element
   */
  const resolveItemComponent = (
    slide: Slide | APIBeforeAfterPhoto,
    isThumbnail = false,
  ): JSX.Element => {
    const additionalProps = {
      isThumbnail,
      variant,
    };

    switch (itemVariant) {
      case CarouselItemVariants.BEFORE_AFTER:
        return (
          <BeforeAfter.Item
            slide={slide as APIBeforeAfterPhoto}
            {...props}
            {...additionalProps}
          />
        );
      default:
        return (
          <Default.Item
            slide={slide as Slide}
            {...props}
            {...additionalProps}
          />
        );
    }
  };

  /**
   * Update the slides by the given treatment filter value
   */
  const updateResultsByTreatment = (option: Record<string, string>): void => {
    if (option.value === 'All') {
      return setCarouselSlides(originalSlides);
    }

    const filteredSlides = slides.filter((slide) => {
      return (
        (slide as APIBeforeAfterPhoto).beforeafterphoto?.treatment ===
        option.value
      );
    });
    setCarouselSlides(filteredSlides);
  };

  switch (variant) {
    case CarouselVariants.ALTERNATE:
      return (
        <AlternateCarousel
          resolveItemComponent={resolveItemComponent}
          itemVariant={itemVariant}
          updateResultsByTreatment={updateResultsByTreatment}
          slides={carouselSlides}
          hasAllImagesLoaded={hasAllImagesLoaded}
          isLoading={isLoading}
          onClose={onClose}
          {...props}
        />
      );
    default:
      return (
        <DefaultCarousel
          resolveItemComponent={resolveItemComponent}
          itemVariant={itemVariant}
          updateResultsByTreatment={updateResultsByTreatment}
          slides={carouselSlides}
          originalSlides={originalSlides}
          hasAllImagesLoaded={hasAllImagesLoaded}
          isLoading={isLoading}
          onClose={onClose}
          {...props}
        />
      );
  }
};

export default Carousel;
